<template>
  <div>
    <b-modal v-if="shouldShowRejectionModal && selectedItemToReject" title="Please add the reason to reject" v-model="shouldShowRejectionModal" size="sm" centered no-close-on-backdrop @cancel="shouldShowRejectionModal = false" @close="shouldShowRejectionModal = false" @ok="rejectPermission">
      <div>
        <p>
          <b>Customer Number:</b> {{ selectedItemToReject.CustomerNumber }}
        </p>
        <p>
          <b>Customer Name:</b> {{ selectedItemToReject.CustomerName }}
        </p>
        <p>
          <b>User Email:</b> {{ selectedItemToReject.UserEmail }}
        </p>
        <b-form-group label="Rejection reason*" label-for="rejection-reason">
          <b-form-input id="rejection-reason" v-model="rejectionReason" placeholder="Rejection reason" :state="isValidRejectionReason()"></b-form-input>
          <b-form-invalid-feedback>Please add a rejection reason</b-form-invalid-feedback>
        </b-form-group>
      </div>
    </b-modal>
    <b-modal
      v-model="showApproveModal"
      v-if="selectedItemToApprove != null"
      title="Are you sure you want to approve?"
      size="sm"
      centered
      no-close-on-backdrop
      @cancel="showApproveModal = false"
      @close="showApproveModal = false"
      @ok="approvePermission"
    >
      <div>
        <p>
          <b>Customer Number:</b> {{ selectedItemToApprove.CustomerNumber }}
        </p>
        <p>
          <b>Customer Name:</b> {{ selectedItemToApprove.CustomerName }}
        </p>
        <p>
          <b>User Email:</b> {{ selectedItemToApprove.UserEmail }}
        </p>
      </div>
    </b-modal>
    <div v-if="customerLoading">
      <loading-overlay :isLoading="customerLoading" :loadingText="'Loading Customer'" />
    </div>
    <div v-else-if="errorMessage !== ''" class="d-flex justify-content-center">
      <b-card title="Error" class="p-5">
        <b-card-text>{{ errorMessage }}</b-card-text>
      </b-card>
    </div>
    <div v-else-if="customer.CustomerNumber === ''" class="d-flex justify-content-center">
      <b-card title="404" class="p-5">
        <b-card-text>Error - Customer not found</b-card-text>
      </b-card>
    </div>
    <div v-else>
      <b-row class="align-items-center pb-3 my-1">
        <b-col md="12">
          <h5>
            <span v-if="_hasHistory()" class="mr-4 primary-color back-btn" @click="$router.back()">
              <b-icon icon="arrow-left"></b-icon>
            </span>
            <i class="fa fa-user fa-lg mr-2"></i>Customer Profile
          </h5>
        </b-col>
      </b-row>
      <b-card header-bg-variant="primary" header-text-variant="white">
        <template #header>
          <h3 class="mb-0">{{ customer.CustomerName }} - {{ customer.CustomerNumber }}</h3>
        </template>

        <b-tabs content-class="mt-3" justified lazy>
          <b-tab title="Customer Information">
            <b-row>
              <b-col md="12" class="section-header">
                <b>Address</b>
              </b-col>
              <b-col md="12" class="section-info">
                <b-row class="my-1">
                  <b-col cols="4">Name:</b-col>
                  <b-col cols="8">{{ customer.CustomerAddress.Name1 }}</b-col>
                </b-row>
                <b-row class="my-1">
                  <b-col cols="4">Street:</b-col>
                  <b-col cols="8">
                    {{ customer.CustomerAddress.Street }}
                    <p v-if="customer.CustomerAddress.Name2 !== ''">{{ customer.CustomerAddress.Name2 }}</p>
                  </b-col>
                </b-row>
                <b-row class="my-1">
                  <b-col cols="4">Postal Code/City:</b-col>
                  <b-col cols="4">{{ customer.CustomerAddress.PostalCode }}</b-col>
                  <b-col cols="4">{{ customer.CustomerAddress.City }}</b-col>
                </b-row>
                <b-row class="my-1">
                  <b-col cols="4">Country/Region:</b-col>
                  <b-col cols="4">{{ customer.CustomerAddress.Country }}</b-col>
                  <b-col cols="4">{{ customer.CustomerAddress.Region }}</b-col>
                </b-row>
                <b-row class="my-1" v-if="customer.CustomerAddress.TaxJurisdictionCode !== undefined">
                  <b-col cols="4">Tax Jurisdiction Code:</b-col>
                  <b-col cols="8">{{ customer.CustomerAddress.TaxJurisdictionCode }}</b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12" class="section-header">
                <b>Communication</b>
              </b-col>
              <b-col md="12" class="section-info">
                <b-row>
                  <b-col cols="4">Phone #:</b-col>
                  <b-col cols="8">{{ customer.CustomerAddress.Phone }}</b-col>
                </b-row>
                <b-row>
                  <b-col cols="4">Email:</b-col>
                  <b-col cols="8">
                    <div v-for="mail in customer.CustomerAddress.Emails" :key="mail.Email">{{ mail.Email }}</div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row v-if="_isCustomerAdminSite()">
              <b-col md="12" class="section-header">
                <b>Sales Representatives</b>
              </b-col>
              <b-col md="12" class="section-info">
                <template v-if="customer.SalesReps && customer.SalesReps.length > 0">
                  <b-row v-for="salesRep in customer.SalesReps" :key="salesRep.SalesRepNumber">
                    <b-col>
                      <router-link
                        :to="{
                          name: _isCustomerAdminSite() ? 'sales-rep-profile' : 'sales-rep-profile-single',
                          params: { salesRepNumber: salesRep.SalesRepNumber },
                        }"
                      >
                        {{ salesRep.SalesRepNumber }}
                      </router-link>
                      - {{ salesRep.Name }}
                    </b-col>
                  </b-row>
                </template>
                <template v-else>
                  The customer doesn't have any sales representative associated.
                </template>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab title="Partner Information">
            <b-row>
              <b-col md="12">
                <b-row>
                  <b-col md="12" class="section-header">
                    <b>Bill To:</b>
                  </b-col>
                  <b-col md="12" class="section-info">
                    <div class="bordered-container">
                      <div>
                        <span>{{ customer.CustomerNumber }}</span>
                      </div>
                      <div>
                        <span>{{ customer.CustomerAddress.Name1 }}</span>
                      </div>
                      <div>
                        <span>{{ customer.CustomerAddress.Street }}</span>
                      </div>
                      <div>
                        <span>{{ customer.CustomerAddress.City }}, {{ regionName(customer.CustomerAddress.Region) }}, {{ customer.CustomerAddress.PostalCode }}</span>
                      </div>
                      <div>
                        <span>{{ countryName(customer.CustomerAddress.Country) }}</span>
                      </div>
                      <div>
                        <span>Tel: {{ customer.CustomerAddress.Phone || 'N/A' }}</span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
              <b-col md="12">
                <b-row>
                  <b-col md="12" class="section-header">
                    <b>Ship To:</b>
                  </b-col>
                  <b-col md="5"  class="section-info" v-if="customerShipTos.length > 1">
                    <b-form-input
                      type="text"
                      class="form-control search-input"
                      placeholder="Search for..."
                      v-model="shipToSearch"
                    />
                    <div style="max-height: 300px; overflow:auto">
                      <b-list-group>
                        <b-list-group-item v-for="shipTo in customerShipToFiltered" :key="shipTo.CustomerNumber" :active="shipTo === selectedShipTo" button @click="selectedShipTo = shipTo">
                          {{ customLabel(shipTo) }}
                        </b-list-group-item>
                      </b-list-group>
                    </div>
                  </b-col>
                  <b-col :md="customerShipTos.length > 1 ? '7' : '12'" class="section-info">
                    <div class="bordered-container" v-if="selectedShipTo">
                      <div>
                        <router-link
                          :to="{
                            name: 'customer',
                            params: { customerNumber: selectedShipTo.CustomerNumber },
                          }"
                        >
                          <span>{{ selectedShipTo.CustomerNumber }}</span>
                        </router-link>
                      </div>
                      <div>
                        <span>{{ selectedShipTo.CustomerAddress.Name1 }}</span>
                      </div>
                      <div>
                        <span>{{ selectedShipTo.CustomerAddress.Street }}</span>
                      </div>
                      <div>
                        <span>{{ selectedShipTo.CustomerAddress.City }}, {{ regionName(selectedShipTo.CustomerAddress.Region) }}, {{ selectedShipTo.CustomerAddress.PostalCode }}</span>
                      </div>
                      <div>
                        <span>{{ countryName(selectedShipTo.CustomerAddress.Country) }}</span>
                      </div>
                      <div>
                        <span>Tel: {{ selectedShipTo.CustomerAddress.Phone || 'N/A' }}</span>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab>
            <template #title>
              <span data-cy="sales-data-tab">Sales Data <i class="fa fa-warning text-warning" v-if="customer.IsCreditBlocked"></i></span>
            </template>
            <b-row>
              <b-col md="12" class="section-header">
                <b>Customer Credit</b>
              </b-col>
              <b-col md="12" class="section-info">
                <b-row :class="{ 'my-1': true, 'text-danger': customer.IsCreditBlocked }">
                  <b-col cols="4">Credit Blocked:</b-col>
                  <b-col cols="8">
                    {{ customer.IsCreditBlocked ? 'Yes' : 'No' }}
                  </b-col>
                </b-row>
                <b-row class="my-1">
                  <b-col cols="4">Credit Limit:</b-col>
                  <b-col cols="8">{{ customer.CreditLimit ? formatPrice(customer.CreditLimit) : '' }}</b-col>
                </b-row>
                <b-row class="my-1">
                  <b-col cols="4">Total Receivables:</b-col>
                  <b-col cols="8">{{ customer.TotalReceivables }}</b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12" class="section-header">
                <b>Multipliers</b>
              </b-col>
              <b-col md="12" class="section-info">
                <b-row class="my-1" v-if="customerMultipliers.CustomerMultiplier">
                  <b-col cols="6">Customer Multiplier:</b-col>
                  <b-col cols="6">{{ getMultiplierPricingRate(customerMultipliers.CustomerMultiplier) }}</b-col>
                </b-row>
                <b-row class="my-1" v-if="customerMultipliers.ProductMultipliers && productMultipliers.length > 0">
                  <b-col cols="12"><b>Product Hierarchy Multipliers</b></b-col>
                  <b-col cols="12" style="max-height: 200px;overflow:auto">
                    <b-row v-for="multiplier in productMultipliers" :key="multiplier.Number">
                      <b-col cols="6">{{ multiplier.Number }}:</b-col>
                      <b-col cols="6">{{ getMultiplierPricingRate(multiplier) }}</b-col>
                    </b-row>
                  </b-col>
                </b-row>
                <b-row class="my-1" v-if="customerMultipliers.MaterialMultipliers && materialMultipliers.length > 0">
                  <b-col cols="12"><b>Material Multipliers</b></b-col>
                  <b-col cols="12" style="max-height: 200px;overflow:auto">
                    <b-row v-for="multiplier in materialMultipliers" :key="multiplier.Number">
                      <b-col cols="6">{{ multiplier.Number }}:</b-col>
                      <b-col cols="6">{{ getMultiplierPricingRate(multiplier) }}</b-col>
                    </b-row>
                  </b-col>
                </b-row>
                <b-row class="my-1" v-if="customerMultipliers.MaterialMultipliers && materialNetPrices.length > 0">
                  <b-col cols="12"><b>Material Net Prices</b></b-col>
                  <b-col cols="12" style="max-height: 200px;overflow:auto">
                    <b-row v-for="netPrice in materialNetPrices" :key="netPrice.Number">
                      <b-col cols="6">{{ netPrice.Number }}:</b-col>
                      <b-col cols="6">{{ getMultiplierPricingRate(netPrice) }}</b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="12" class="section-header">
                <b>Payment Term</b>
              </b-col>
              <b-col md="12" class="section-info">
                <p class="m-0">{{ customer.PaymentTerm }}</p>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab title="Notes">
            <b-row>
              <b-col md="6">
                  <b-col md="12" class="section-header">
                    <b>Customer Sales Notes</b>
                  </b-col>
                  <b-col md="12" class="section-info">
                    <div class="my-1" v-for="(text, ti) in customer.CustomerSalesText" :key="ti">{{ text }}</div>
                  </b-col>
              </b-col>
              <b-col md="6">
                  <b-col md="12" class="section-header">
                    <b>Customer Shipping Notes</b>
                  </b-col>
                  <b-col md="12" class="section-info">
                    <div class="my-1" v-for="(text, ti) in customer.CustomerShippingText" :key="ti">{{ text }}</div>
                  </b-col>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab title="Permissions">
            <b-row>
              <b-col md="12" class="section-header">
                <b>Users</b>
              </b-col>
              <b-col md="12" class="section-info">
                <template v-if="customer.Permissions && customer.Permissions.length > 0">
                  <b-row v-for="permission in customer.Permissions" :key="permission.Id">
                    <b-col>
                      {{ permission.ResourceName }}
                    </b-col>
                  </b-row>
                </template>
                <template v-else>
                  The customer doesn't have any users.
                </template>
              </b-col>
            </b-row>
            <b-row v-if="_isCustomerAdminSite()">
              <b-col md="12" class="section-header">
                <b>Pending Permission Requests</b>
              </b-col>
              <b-col md="12" class="section-info">
                <template v-if="customer.PendingPermissionRequests && customer.PendingPermissionRequests.length > 0">
                  <b-row v-for="permission in customer.PendingPermissionRequests" :key="permission.Id">
                    <b-col>{{ permission.UserEmail }}</b-col>
                    <template v-if="permission.Status === 5">
                      <b-col><strong>Waiting for Sales Rep Approval</strong></b-col>
                    </template>
                    <template v-else>
                      <b-col>
                        <b-button variant="link" @click="onApprove(permission)">
                          Approve
                        </b-button>
                      </b-col>
                      <b-col>
                        <b-button variant="link" @click="onReject(permission)">
                          Reject
                        </b-button>
                      </b-col>
                    </template>
                  </b-row>
                </template>
                <template v-else>
                  No pending permissions.
                </template>
              </b-col>
            </b-row>
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator'
import MxSite from '@/mixins/site'
import { namespace } from 'vuex-class'
import { ICustomer } from '@/store/customer/types'
import LoadingOverlay from '@/components/utility/loading-overlay.vue'
import { ResourceType } from '@/store/security/types'
import { isCustomerAdminSite, isSalesRepSite } from '@/helpers/sites.helper'
import { Api } from '@/api/Api'
import { HttpMethod } from '@/api/FetchApi'
import { ICustomerMultiplier, IMultiplier } from '@/store/multiplier/types'
import { MultiplierPricingType } from '@/store/product/types'
import Multiselect from 'vue-multiselect'
import { formatPrice } from '@/helpers/currency.helper'

const nsCustomer = namespace('customer')
const nsMultiplier = namespace('multiplier')

@Component({
  components: {
    LoadingOverlay,
    Multiselect,
  },
})
export default class Customer extends Mixins(MxSite) {
  private api = new Api()

  @nsCustomer.Getter('customer')
  public customer!: ICustomer

  @nsCustomer.Getter('customerShipTos')
  public customerShipTos!: ICustomer[]

  @nsCustomer.Getter('customerLoading')
  public customerLoading!: boolean

  @nsCustomer.Getter('errorMessage')
  public errorMessage!: string

  @nsCustomer.Action('getCustomer')
  public getCustomer: any

  @nsCustomer.Action('fetchCustomerShipTos')
  public fetchCustomerShipTos: any

  @nsMultiplier.State('customerMultipliers')
  public customerMultipliers!: ICustomerMultiplier

  public rejectionReason: string | null = null

  public shouldShowRejectionModal = false
  public showApproveModal = false

  public selectedItemToReject: { Id: string, UserEmail: string, CustomerName: string, CustomerNumber: string } | null = null
  public selectedItemToApprove: { Id: string, UserEmail: string, CustomerName: string, CustomerNumber: string } | null = null
  public countries: Array<any> = []
  public states: Array<any> = []
  public selectedShipTo: ICustomer | null = null
  public shipToSearch: string = ''
  public formatPrice = formatPrice

  public async mounted() {
    this.hideBreadcrumbDisplay(true)
    const customerNumber = this.customerNumber
    await this.loadCustomer(customerNumber)
    await this.fetchCountries()
    await this.fetchUSStates()
  }

  public async loadCustomer(customerNumber) {
    await this.getCustomer(customerNumber)
    await this.fetchCustomerShipTos(customerNumber).then(() => {
      this.selectedShipTo = this.customerShipTos.length > 0 ? this.customerShipTos[0] : null
    })
    await this.$store.dispatch('multiplier/fetchCustomerMultipliers', customerNumber)
  }

  @Watch('customerNumber')
  private async customerNumberChanged(customerNumber) {
    await this.loadCustomer(customerNumber)
  }

  get customerShipToFiltered() {
    if (this.shipToSearch.length > 0) {
      const keyword = new RegExp(this.shipToSearch, 'i')
      return this.customerShipTos.filter(s => s.CustomerName.search(keyword) >= 0 || s.CustomerNumber.search(keyword) >= 0)
    }
    return this.customerShipTos
  }

  async fetchCountries() {
    try {
      await this.$store.dispatch('common/fetchCountries', '')
      this.countries = this.$store.getters['common/countries']
    } catch (error) {
      console.error('Error fetching countries:', error)
    }
  }

  async fetchUSStates() {
    try {
      await this.$store.dispatch('common/fetchCountryStates', 'US') // get states for US for now
      this.states = this.$store.getters['common/countryStates']
    } catch (error) {
      console.error('Error fetching country states:', error)
    }
  }

  get customerNumber() {
    return this.$route.params.customerNumber
  }

  public customLabel({
    CustomerName,
    CustomerNumber,
  }: {
    CustomerName: string
    CustomerNumber: string
  }) {
    return `${CustomerName} - ${CustomerNumber}`
  }

  public get userSalesRepPermissions() {
    const permissions = (this.currentUser.ResourcePermissions ?? []).filter(item => item.Type === ResourceType.SalesRep)

    return permissions
  }

  public countryName(countryName) {
    let name = this.countries.find(x => x.AlphaCode === countryName)?.Name
    if (!name) {
      name = countryName
    }
    return name
  }

  public regionName(regionName) {
    let name = this.states.find(x => x.AlphaCode === regionName)?.Name
    if (!name) {
      name = regionName
    }
    return name
  }

  public _isCustomerAdminSite() {
    return isCustomerAdminSite(this.role)
  }

  public _isSalesRepSite() {
    return isSalesRepSite(this.site.Name)
  }

  public onReject(item: any) {
    this.selectedItemToReject = item
    this.shouldShowRejectionModal = true
  }

  public rejectPermission() {
    const obj = { isApproved: false, rejectionStatus: this.rejectionReason, PermissionIds: [this.selectedItemToReject?.Id] }
    this.api.fetchV2({
      url: '/customer/permission-requests/batchRespond',
      obj,
      method: HttpMethod.Post,
    }).then(() => {
      this.toastSuccess('Updated successfully')
      const customerNumber = this.customerNumber
      this.getCustomer(customerNumber)
    })
  }

  public onApprove(item: any) {
    this.selectedItemToApprove = item
    this.showApproveModal = true
  }

  public approvePermission() {
    this.api.fetchV2({
      url: '/customer/permission-requests/batchRespond',
      obj: { isApproved: true, PermissionIds: [this.selectedItemToApprove?.Id] },
      method: HttpMethod.Post,
    }).then(() => {
      this.toastSuccess('Updated successfully')
      const customerNumber = this.customerNumber
      this.getCustomer(customerNumber)
    })
  }

  public isValidRejectionReason() {
    return this.rejectionReason && this.rejectionReason !== ''
  }

  public getMultiplierPricingRate(multiplier: IMultiplier) {
    if (multiplier.PricingType === MultiplierPricingType.NetPrice) {
      return `$${multiplier.Rate.toFixed(2)}`
    }
    return `${multiplier.Rate}%`
  }

  public get productMultipliers(): IMultiplier[] {
    if (this.customerMultipliers.ProductMultipliers) {
      return Object.values(this.customerMultipliers.ProductMultipliers)
    }
    return []
  }

  public get materialMultipliers(): IMultiplier[] {
    if (this.customerMultipliers.MaterialMultipliers) {
      const vals: IMultiplier[] = Object.values(this.customerMultipliers.MaterialMultipliers)
      return vals.filter(s => s.PricingType === MultiplierPricingType.Multiplier)
    }
    return []
  }

  public get materialNetPrices(): IMultiplier[] {
    if (this.customerMultipliers.MaterialMultipliers) {
      const vals: IMultiplier[] = Object.values(this.customerMultipliers.MaterialMultipliers)
      return vals.filter(s => s.PricingType === MultiplierPricingType.NetPrice)
    }
    return []
  }
}
</script>
<style scoped>
.section-header {
  background-color: #e7f0f8;
  border-color: #a6c2ef;
  padding: 0.25rem 0.5rem;
}

.section-info {
  min-height: 30px;
  padding: 5px 10px;
}

.bordered-container {
  margin-bottom:20px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  position: relative;
  padding: 15px;
}
</style>
